import React, { useContext, useState, useMemo, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { API } from "aws-amplify";
import Context from "../../../Context/Context";
import PaginationComponent from "../../../common/Pagination";
import { toast } from "react-toastify";
import { FaFilter, FaChevronDown } from "react-icons/fa";
import ClassModal from "../UpcomingSessions/Components/CreateEditClassModal";
import PreviousClassTable from "./Components/PreviousClassTable";
import UpdateRecordingModal from "./Components/UpdateRecordingModal";

// Custom Dropdown Component for Filters
const CustomFilterDropdown = ({ 
  value, 
  onChange, 
  options, 
  placeholder, 
  isDarkMode, 
  disabled = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const portalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current && !buttonRef.current.contains(event.target) &&
        portalRef.current && !portalRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen && buttonRef.current) {
      document.addEventListener('mousedown', handleClickOutside);
      const rect = buttonRef.current.getBoundingClientRect();
      
      // Always position below the button
      setPosition({
        top: rect.bottom + 4,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - rect.width - 8)),
        width: rect.width,
      });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value) || null;
  const displayValue = selectedOption ? selectedOption.label : placeholder;
  const clickableOptions = options.filter(opt => !opt.disabled);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full h-9 md:h-11 pl-3 md:pl-4 pr-8 md:pr-10 py-1.5 md:py-2 rounded-lg text-xs md:text-sm transition-all duration-200 text-left flex items-center justify-between ${
          isDarkMode 
            ? 'bg-[#1a1a1a] text-white hover:bg-[#222222] focus:outline-none focus:ring-2 focus:ring-[#FDCF08]' 
            : 'bg-white border border-gray-200 text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#FDCF08]'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${
          !selectedOption ? (isDarkMode ? 'text-gray-400' : 'text-gray-500') : ''
        }`}
      >
        <span className="truncate">{displayValue}</span>
        <FaChevronDown 
          className={`ml-2 text-xs transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} 
        />
      </button>
      
      {isOpen && !disabled && createPortal(
        <div 
          ref={portalRef}
          className={`fixed z-[99999] rounded-lg shadow-xl overflow-hidden border ${
            isDarkMode ? 'bg-[#1a1a1a] border-[#2a2a2a]' : 'bg-white border-gray-200'
          }`}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="max-h-60 overflow-y-auto">
            {clickableOptions.length === 0 ? (
              <div className={`px-4 py-3 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                No options available
              </div>
            ) : (
              clickableOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange({ target: { value: option.value } });
                    setIsOpen(false);
                  }}
                  className={`w-full px-4 py-2.5 text-sm text-left transition-colors duration-150 ${
                    value === option.value
                      ? isDarkMode
                        ? 'bg-[#2a2a2a] text-[#FDCF08] font-medium'
                        : 'bg-[#FDCF08]/10 text-[#FDCF08] font-medium'
                      : isDarkMode
                      ? 'text-white hover:bg-[#2a2a2a]'
                      : 'text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const PreviousSessions = () => {
  const [recordingLink, setRecordingLink] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [classTypeFilter, setClassTypeFilter] = useState("");
  const [instructorTypeFilter, setInstructorTypeFilter] = useState("");
  const Ctx = useContext(Context);
  const UtilCtx = useContext(Context).util;
  const { loadingPrevious } = Ctx;
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [modal, setModal] = useState(false);
  const [modalMode, setModalMode] = useState("edit"); // 'edit' or 'view'
  const [selectedClass, setSelectedClass] = useState(null);
  const userType = Ctx.userData.userType;

  const handleClassAction = (classData, mode) => {
    setSelectedClass(classData);
    setModalMode(mode);
    setModal(true);
  };

  const [selectedClassId, setSelectedClassId] = useState(null);

  const onPageChange = (page) => {
    setCurrentPage(page);
  };

  const filteredClasses = useMemo(() => {
    return Ctx.previousClasses.filter(
      (clas) =>
        (instructorTypeFilter === "" ||
          clas.instructorNames === instructorTypeFilter) &&
        (classTypeFilter === "" || clas.classType === classTypeFilter)
    );
  }, [Ctx.previousClasses, instructorTypeFilter, classTypeFilter]);

  // Remove duplicates
  function removeDuplicateClasses(filtered) {
    const map = new Map();
    filtered.forEach((item) => {
      const key = `${item.date}|${item.instructorNames}|${item.classType}`;
      if (!map.has(key)) {
        map.set(key, item);
      }
    });
    return Array.from(map.values());
  }

  const uniqueFilteredClasses = removeDuplicateClasses(filteredClasses);

  const itemsPerPage = 5;
  const totalPages = Math.ceil(uniqueFilteredClasses.length / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedClasses = uniqueFilteredClasses.slice(startIndex, endIndex);
  const classTypeNameArray = [
    "Choreography by Anupam",
    "Instructor Training",
    "Cancellation",
  ];

  const classTypes = Array.from(
    new Set(filteredClasses.map((clas) => clas.classType))
  );

  const getInstructor = (name) => {
    return Ctx.instructorList.find(
      (i) => i.name?.toString().trim() === name?.toString().trim()
    );
  };
  const onRecordingUpdate = async (e) => {
    e.preventDefault();
    UtilCtx.setLoader(true);

    try {
      if (!selectedClassId || recordingLink.length === 0) {
        toast.warn("Please enter a recording link");
        UtilCtx.setLoader(false);
        return;
      }

      await API.put("user", "/admin/edit-schedule-recording/Bworkz", {
        body: { classId: selectedClassId, recordingLink },
      });

      const updatedClasses = Ctx.previousClasses.map((clas) =>
        clas.classId === selectedClassId ? { ...clas, recordingLink } : clas
      );

      Ctx.setPreviousClasses(updatedClasses);
      setSelectedClassId(null);
      setRecordingLink("");
      setShowUpdateModal(false);
      toast.success("Recording Link Updated Successfully");
    } catch (e) {
      toast.error(e.message);
    } finally {
      UtilCtx.setLoader(false);
    }
  };

  const handleUpdateClick = (classId, currentLink) => {
    setSelectedClassId(classId);
    setRecordingLink(currentLink || "");
    setShowUpdateModal(true);
  };

  const sortedPreviousClasses = useMemo(() => {
    return [...filteredClasses].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [filteredClasses]);

  const isDarkMode = Ctx.isDarkMode ?? true;

  const instructors = useMemo(
    () =>
      Array.from(
        new Set(Ctx.previousClasses.map((clas) => clas.instructorNames))
      ),
    [Ctx.previousClasses]
  );

  return (
    <>
      <div className={`w-full md:w-[calc(100%-5rem)] mx-auto mt-0 md:mt-10 flex flex-col h-full transition-colors duration-200 ${isDarkMode ? 'bg-black' : 'bg-white'}`} style={{ fontFamily: "Inter, sans-serif" }}>
        {/* Header Section */}
        <div className={`w-full px-3 md:px-8 py-3 md:py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4 border-b ${isDarkMode ? 'border-[#2a2a2a]' : 'border-gray-200'}`}>
          <div className="flex items-center gap-2 sm:gap-4">
            <h1 className={`text-xl md:text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-black'}`}>Previous Classes</h1>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg flex items-center gap-2 transition-colors text-xs md:text-sm font-semibold ${
              showFilters
                ? 'bg-[#FDCF08] text-black hover:bg-[#f0c000]'
                : 'bg-[#FDCF08] text-black hover:bg-[#f0c000]'
            }`}
          >
            <FaFilter size={14} />
            Filter
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className={`w-full px-3 md:px-8 py-4 md:py-5 border-b ${isDarkMode ? 'bg-[#0f0f0f] border-[#2a2a2a]' : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex flex-col sm:flex-row gap-4 md:gap-6">
              <div className="flex flex-col gap-1.5 md:gap-2 flex-1">
                <label className={`text-xs md:text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                  Instructor
                </label>
                <CustomFilterDropdown
                  value={instructorTypeFilter}
                  onChange={(e) => {
                    setCurrentPage(1);
                    setInstructorTypeFilter(e.target.value);
                  }}
                  options={[
                    { value: "", label: "All Instructors" },
                    ...instructors.map((instructorNames) => ({
                      value: instructorNames,
                      label: instructorNames
                    }))
                  ]}
                  placeholder="All Instructors"
                  isDarkMode={isDarkMode}
                />
              </div>

              <div className="flex flex-col gap-1.5 md:gap-2 flex-1">
                <label className={`text-xs md:text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                  Class Type
                </label>
                <CustomFilterDropdown
                  value={classTypeFilter}
                  onChange={(e) => {
                    setCurrentPage(1);
                    setClassTypeFilter(e.target.value);
                  }}
                  options={[
                    { value: "", label: "All Classes" },
                    ...classTypeNameArray.map((classType) => ({
                      value: classType,
                      label: classType
                    }))
                  ]}
                  placeholder="All Classes"
                  isDarkMode={isDarkMode}
                />
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto px-3 md:px-8 py-3 md:py-6">
          <PreviousClassTable
            classes={paginatedClasses}
            userType={userType}
            instructorList={Ctx.instructorList}
            getInstructor={getInstructor}
            onAction={handleClassAction}
            onUpdateRecording={handleUpdateClick}
            isDarkMode={isDarkMode}
          />

          <div className="mt-4 md:mt-6">
            <PaginationComponent
              showIcons={true}
              data={uniqueFilteredClasses}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={onPageChange}
            />
          </div>
        </div>
      </div>

      {/* Update Recording Modal */}
      <UpdateRecordingModal
        showUpdateModal={showUpdateModal}
        setShowUpdateModal={setShowUpdateModal}
        recordingLink={recordingLink}
        setRecordingLink={setRecordingLink}
        onRecordingUpdate={onRecordingUpdate}
        selectedClassId={selectedClassId}
        setSelectedClassId={setSelectedClassId}
        isDarkMode={isDarkMode}
      />
      <ClassModal
        isOpen={modal}
        onClose={() => {
          setModal(false);
          setSelectedClass(null);
        }}
        classTypeNameArray={classTypeNameArray}
        instructorList={Ctx.instructorList}
        userData={Ctx.userData}
        upcomingClasses={Ctx.previousClasses}
        util={UtilCtx}
        classData={selectedClass}
        setModalMode={setModalMode}
        readOnly={modalMode === "view"}
        loadingPrevious={loadingPrevious}
        setUpcomingClasses={Ctx.setPreviousClasses}
      />
    </>
  );
};

export default PreviousSessions;
